// A tappable list row, the phone's way to open something or run an action from a list: the whole
// row is the target (at least 56 px high, ripple edge to edge across the 16 px gutters), a leading
// mark, a label with an optional muted detail, and a chevron when it opens another screen. Rows are
// split by 1 px hairlines, sections sit on the bare ground (DESIGN.md).
import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";

import { ChevronIcon } from "./icons/ChevronIcon";
import { PressableTarget } from "./PressableTarget";
import { Text } from "./Text";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: t.space.md,
      minHeight: 56,
      marginHorizontal: -t.space.lg,
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.sm,
      borderTopWidth: t.borderWidth.card,
      borderTopColor: t.color.border,
    },
    text: { flex: 1, gap: t.space.xxs },
    head: { flexDirection: "row", alignItems: "center", gap: t.space.sm },
    disabled: { opacity: 0.6 },
  }),
);

export type ListRowProps = {
  label: string;
  /** Muted lines under the label. */
  detail?: string | readonly string[];
  onPress: () => void;
  /** A mark before the text (avatar, icon). */
  leading?: ReactNode;
  /** A small mark after the label (e.g. the Default badge). */
  badge?: ReactNode;
  /** True when the row opens another screen. */
  chevron?: boolean;
  /** `danger` for removing or leaving; `action` for a blue command such as "add a child". */
  tone?: "default" | "action" | "danger";
  disabled?: boolean;
  /** The spoken name when the label alone isn't enough ("Edit Aarav Sharma"). */
  accessibilityLabel?: string;
  testID?: string;
};

export function ListRow({
  label,
  detail,
  onPress,
  leading,
  badge,
  chevron = false,
  tone = "default",
  disabled = false,
  accessibilityLabel,
  testID,
}: ListRowProps) {
  const styles = useStyles();
  const theme = useTheme();
  const lines = detail === undefined ? [] : [detail].flat();
  const color =
    tone === "danger"
      ? theme.color.danger
      : tone === "action"
        ? theme.color.primaryText
        : theme.color.text;
  return (
    <PressableTarget
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      // The muted lines are read after the name ("Change school", then the school).
      accessibilityHint={lines.length > 0 ? lines.join(", ") : undefined}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[styles.row, disabled && styles.disabled]}
      hoveredStyle={{ backgroundColor: theme.color.surface }}
      testID={testID}
    >
      {leading}
      <View style={styles.text}>
        <View style={styles.head}>
          <Text weight="semiBold" style={{ color }}>
            {label}
          </Text>
          {badge}
        </View>
        {lines.map((line) => (
          <Text key={line} tone="muted" size="sm">
            {line}
          </Text>
        ))}
      </View>
      {chevron ? (
        <ChevronIcon color={theme.color.textMuted} size={theme.size.icon.md} />
      ) : null}
    </PressableTarget>
  );
}
